import { useTranslation } from 'react-i18next';
import { SEO } from '../components/shared/SEO';
import { useNavigate } from 'react-router-dom';
import { useLegalEntity } from '../hooks/useLegalEntity';

export const TermsAndConditionsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { entity, loading, fullAddress, taxLine } = useLegalEntity();

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 font-sans text-slate-800">
            <SEO
                title={t('legal_pages.terms_conditions.title')}
                description="Termini e condizioni di servizio di Deskbay."
                noindex={true}
            />
            <div className="max-w-4xl mx-auto bg-white p-8 sm:p-12 shadow-sm rounded-2xl">

                <h1 className="text-4xl font-bold mb-2">{t('legal_pages.terms_conditions.title')}</h1>
                <p className="text-slate-500 mb-8">
                    {t('legal_pages.terms_conditions.last_updated', { date: new Date().toLocaleDateString() })}
                </p>

                <div className="prose prose-slate max-w-none">

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.terms_conditions.acceptance.title')}
                        </h2>
                        <p>
                            {t('legal_pages.terms_conditions.acceptance.text', {
                                company_name: entity.company_name,
                                website: entity.website
                            })}
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.terms_conditions.service_description.title')}
                        </h2>
                        <p>{t('legal_pages.terms_conditions.service_description.intro')}</p>
                        <ul className="list-disc pl-5 mt-2 space-y-2">
                            <li>{t('legal_pages.terms_conditions.service_description.feature1')}</li>
                            <li>{t('legal_pages.terms_conditions.service_description.feature2')}</li>
                            <li>{t('legal_pages.terms_conditions.service_description.feature3')}</li>
                            <li>{t('legal_pages.terms_conditions.service_description.feature4')}</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.terms_conditions.account_registration.title')}
                        </h2>
                        <p>
                            {t('legal_pages.terms_conditions.account_registration.text', {
                                company_name: entity.company_name
                            })}
                        </p>
                    </section>

                    <section className="mb-8">
                        <h3 className="text-xl font-semibold mb-3 text-slate-800">
                            {t('legal_pages.terms_conditions.restaurant_ownership.title')}
                        </h3>
                        <p>
                            {t('legal_pages.terms_conditions.restaurant_ownership.text', {
                                company_name: entity.company_name
                            })}
                        </p>
                    </section>

                    <section className="mb-8">
                        <h3 className="text-xl font-semibold mb-3 text-slate-800">
                            {t('legal_pages.terms_conditions.customer_data_responsibility.title')}
                        </h3>
                        <p>
                            {t('legal_pages.terms_conditions.customer_data_responsibility.text', {
                                company_name: entity.company_name
                            })}
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.terms_conditions.intellectual_property.title')}
                        </h2>
                        <h3 className="text-xl font-semibold mb-2 text-slate-800">
                            {t('legal_pages.terms_conditions.intellectual_property.our_rights_title')}
                        </h3>
                        <p>
                            {t('legal_pages.terms_conditions.intellectual_property.our_rights_text', {
                                company_name: entity.company_name
                            })}
                        </p>
                        <h3 className="text-xl font-semibold mb-2 mt-4 text-slate-800">
                            {t('legal_pages.terms_conditions.intellectual_property.your_content_title')}
                        </h3>
                        <p>
                            {t('legal_pages.terms_conditions.intellectual_property.your_content_text')}
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.terms_conditions.payments_refunds.title')}
                        </h2>
                        <ul className="list-disc pl-5 mt-2 space-y-2">
                            <li>{t('legal_pages.terms_conditions.payments_refunds.subscriptions')}</li>
                            <li>{t('legal_pages.terms_conditions.payments_refunds.auto_renewal')}</li>
                            <li>{t('legal_pages.terms_conditions.payments_refunds.refund_policy')}</li>
                            <li>{t('legal_pages.terms_conditions.payments_refunds.price_changes')}</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.terms_conditions.liability_limitation.title')}
                        </h2>
                        <p>{t('legal_pages.terms_conditions.liability_limitation.intro')}</p>
                        <ul className="list-disc pl-5 mt-2 space-y-2">
                            <li>{t('legal_pages.terms_conditions.liability_limitation.no_warranty')}</li>
                            <li>{t('legal_pages.terms_conditions.liability_limitation.no_consequential')}</li>
                            <li>{t('legal_pages.terms_conditions.liability_limitation.max_liability')}</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.terms_conditions.termination.title')}
                        </h2>
                        <p>{t('legal_pages.terms_conditions.termination.our_right')}</p>
                        <p className="mt-4" dangerouslySetInnerHTML={{
                            __html: t('legal_pages.terms_conditions.termination.gdpr_deletion', {
                                email: `<a href="mailto:${entity.email_info}" class="text-sky-600 hover:underline">${entity.email_info}</a>`
                            })
                        }} />
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.terms_conditions.terms_changes.title')}
                        </h2>
                        <p>{t('legal_pages.terms_conditions.terms_changes.text')}</p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.terms_conditions.governing_law.title')}
                        </h2>
                        <p>
                            {t('legal_pages.terms_conditions.governing_law.text', {
                                country: entity.country || 'Italia',
                                city: entity.address_city || 'Roma',
                                province: entity.address_province || 'RM'
                            })}
                        </p>
                    </section>

                    <section className="mb-8 border-t border-slate-200 pt-8">
                        <h2 className="text-2xl font-semibold mb-4 text-slate-900">
                            {t('legal_pages.terms_conditions.contacts.title')}
                        </h2>
                        <p>
                            <strong>{entity.company_name}</strong><br />
                            {fullAddress}<br />
                            {taxLine}<br />
                            {t('legal_pages.terms_conditions.contacts.email_label')} <a href={`mailto:${entity.email_info}`} className="text-sky-600 hover:underline">{entity.email_info}</a>
                            {entity.phone && (<><br />{t('legal_pages.terms_conditions.contacts.phone_label')} {entity.phone}</>)}
                        </p>
                    </section>
                </div>
            </div>
            
            {/* Back Button */}
            <div className="fixed bottom-8 left-8 z-50">
                <button
                    onClick={() => navigate(-1)}
                    className="w-14 h-14 rounded-full shadow-[8px_8px_40px_rgba(0,0,0,0.4),4px_4px_20px_rgba(0,0,0,0.3)] bg-white/95 backdrop-blur-md border border-slate-200/50 hover:bg-slate-50 hover:shadow-[12px_12px_60px_rgba(0,0,0,0.5),6px_6px_30px_rgba(0,0,0,0.4)] hover:scale-105 transition-all duration-300 flex items-center justify-center group"
                >
                    <svg className="w-6 h-6 text-slate-700 group-hover:text-slate-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            </div>
        </div>
    );
};
